import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { RecruitingActions } from '../actions';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromDealsSelectors from '../selectors/deals.selector';
import { RecruitingDealsService } from 'src/app/recruiting/deals/deals.service';
import { GridLookups } from 'src/app/shared/models/offers.model';
import * as selectors from 'src/app/root-store/recruiting-store/selectors/deals.selector';
import { formatDate } from '@angular/common';
import { downloadFile } from 'src/app/shared/utilities/download-file';
import { RecruitingService } from 'src/app/recruiting/recruiting.service';
import { HubConnectionService } from 'src/app/shared/services/realtime-connection.service';
import { RecruitingHubMessage } from '../../../shared/services/realtime-constants';
import { RecruitingDealAlertDto } from '../../../shared/models/recruiting-deal-alert-model';
import { HttpResponseHelpers } from 'src/app/shared/helpers';
import { IdentityService } from '../../../core/identity.service';
import { SharedOffersService } from 'src/app/shared/offers-shared/offers.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';

@Injectable()
export class DealsEffects {
    loadGridData$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(RecruitingActions.DealsActions.setOffersGridSearchQuery),
                concatLatestFrom(() => this._store.select(fromDealsSelectors.selectDealsGridSearchQuery)),
                switchMap(([, query]) => {
                    return forkJoin([this.dealsService.getGridData(query), this.dealsService.lookups()]).pipe(
                        switchMap((joinResult: any) => {
                            const [offers, lookups] = joinResult;
                            return [
                                RecruitingActions.DealsActions.loadOffersSuccess({ offers }),
                                RecruitingActions.DealsActions.loadOfferLookupsSuccess({ lookups })
                            ];
                        }),
                        catchError((error) =>
                            of(
                                RecruitingActions.DealsActions.loadOffersFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    );
                })
            );
        },
        { dispatch: true }
    );

    getLookups$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(RecruitingActions.DealsActions.loadOfferLookups),
            switchMap(() =>
                this.dealsService.lookups().pipe(
                    map((lookups: GridLookups) => RecruitingActions.DealsActions.loadOfferLookupsSuccess({ lookups })),
                    catchError((error) =>
                        of(
                            RecruitingActions.DealsActions.loadOfferLookupsFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                )
            )
        );
    });

    exportDeals$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(RecruitingActions.DealsActions.exportGridData),
            concatLatestFrom(() => this._store.select(selectors.selectDealsGridSearchQuery)),
            switchMap(([action, request]) =>
                this.dealsService.getDealsExport(request).pipe(
                    map((result) => RecruitingActions.DealsActions.exportGridDataSuccess({ result })),
                    catchError((error) =>
                        of(
                            RecruitingActions.DealsActions.exportGridDataFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                )
            )
        );
    });

    downloadExportFile$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(RecruitingActions.DealsActions.exportGridDataSuccess),
                tap((action) => {
                    const date = formatDate(new Date(), 'MM-dd-yyy_hhmmss', 'en');
                    downloadFile(action.result, { fileName: `Deals_${date}.xlsx` });
                })
            );
        },
        { dispatch: false }
    );

    connectToCoreHub$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(RecruitingActions.DealsActions.connectToCoreHub),
            switchMap(() => this._identityService.isAuthenticated$),
            filter((auth) => auth),
            switchMap(() =>
                this._hubConnectionService.coreHub.startConnection().pipe(
                    map(() => RecruitingActions.DealsActions.connectToCoreHubSuccess()),
                    catchError((error) =>
                        of(
                            RecruitingActions.DealsActions.connectToCoreHubFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                )
            )
        );
    });

    disconnectFromCoreHub$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(RecruitingActions.DealsActions.disconnectFromCoreHub),
            switchMap(() =>
                this._hubConnectionService.coreHub.stopConnection().pipe(
                    map(() => RecruitingActions.DealsActions.disconnectFromCoreHubSuccess()),
                    catchError((error) =>
                        of(
                            RecruitingActions.DealsActions.disconnectFromCoreHubFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                )
            )
        );
    });

    isHubConnectionActive$ = createEffect(() => {
        return this._hubConnectionService.coreHub
            .isConnectionActive()
            .pipe(map((isActive) => RecruitingActions.DealsActions.isHubConnectionActive({ isActive })));
    });

    getCandidateOffersRecruiterReviewCount$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(RecruitingActions.DealsActions.getCandidateOffersRecruiterReviewCount),
            switchMap((action) => {
                return this.recruitingService.getCandidateOffersRecruiterReviewCount(action.recruiterId).pipe(
                    map((result) =>
                        RecruitingActions.DealsActions.getCandidateOffersRecruiterReviewCountSuccess({
                            data: { recruiterId: action.recruiterId, deals: result } as RecruitingDealAlertDto
                        })
                    ),
                    catchError((error) =>
                        of(
                            RecruitingActions.DealsActions.getCandidateOffersRecruiterReviewCountFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    recruitingDealAlert$ = createEffect(() => {
        return this._hubConnectionService.coreHub
            .messagesOf<RecruitingDealAlertDto>(RecruitingHubMessage.RECRUITING_DEAL_ALERT)
            .pipe(
                map((data) => {
                    const identity = this._identityService.current;
                    const recruiterId = identity.contactId;
                    if (data.recruiterId === recruiterId) {
                        return RecruitingActions.DealsActions.recruitingDealAlertSuccess({ data: data });
                    } else {
                        return RecruitingActions.DealsActions.noopAction();
                    }
                }),
                catchError((error) =>
                    of(
                        RecruitingActions.DealsActions.recruitingDealAlertFailure({
                            error: HttpResponseHelpers.parseErrorResponse(error)
                        })
                    )
                )
            );
    });

    setOfferAsVerballyAccepted$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(RecruitingActions.DealsActions.setOfferAsVerballyAccepted),
            switchMap((action) => {
                return this._offerService.putToVerballyAcceptedStatus(action.offerId, true).pipe(
                    map(() =>
                        RecruitingActions.DealsActions.setOfferAsVerballyAcceptedSuccess({ offerId: action.offerId })
                    ),
                    catchError(() => of(RecruitingActions.DealsActions.setOfferAsVerballyAcceptedFailure()))
                );
            })
        );
    });

    verbalAcceptanceSuccess$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(RecruitingActions.DealsActions.setOfferAsVerballyAcceptedSuccess),
                tap(() => {
                    this._toasterService.toast('The verbal acceptance of the offer has been shared');
                })
            );
        },
        { dispatch: false }
    );

    verbalAcceptanceFailure$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(RecruitingActions.DealsActions.setOfferAsVerballyAcceptedFailure),
                tap(() => {
                    this._toasterService.toast('Error occurred trying to set offer as verbally accepted', 7000);
                })
            );
        },
        { dispatch: false }
    );

    constructor(
        private readonly _actions$: Actions,
        private readonly dealsService: RecruitingDealsService,
        private readonly recruitingService: RecruitingService,
        private readonly _hubConnectionService: HubConnectionService,
        private readonly _identityService: IdentityService,
        private readonly _offerService: SharedOffersService,
        private readonly _toasterService: ToasterService,
        private readonly _store: Store
    ) {}
}
